import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {AbortController | undefined} */
  #controller;

  connectedCallback() {
    super.connectedCallback();

    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.hasAttribute('data-jesoli-product-gallery')) {
      this.#setupJesoliProductGallery(signal);
    }
  }

  /**
   * Adds product-page-only autoplay and an accessible thumbnail scroll indicator.
   * @param {AbortSignal} signal
   */
  #setupJesoliProductGallery(signal) {
    const slideshow = this.slideshow;
    const controls = this.querySelector('slideshow-controls[thumbnails]:not(.media-gallery__mobile-controls)');
    const thumbnailStrip = controls?.querySelector('.slideshow-controls__thumbnails-container');
    const progress = controls?.querySelector('.jesoli-thumbnail-scrollbar');
    const progressThumb = progress?.querySelector('.jesoli-thumbnail-scrollbar__thumb');
    const desktopMediaQuery = window.matchMedia('(min-width: 750px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    const syncAutoplay = () => {
      if (!slideshow) return;

      if (desktopMediaQuery.matches && !reducedMotionQuery.matches) {
        slideshow.resume();
      } else {
        slideshow.suspend();
      }
    };

    const pauseAfterManualInteraction = (event) => {
      if (!(event.target instanceof Element)) return;
      if (!event.target.closest('slideshow-controls, slideshow-slides')) return;

      slideshow?.pause();
    };

    this.addEventListener('pointerdown', pauseAfterManualInteraction, { signal, capture: true });
    this.addEventListener('keydown', pauseAfterManualInteraction, { signal, capture: true });
    this.addEventListener('focusin', () => slideshow?.suspend(), { signal });
    this.addEventListener(
      'focusout',
      (event) => {
        if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
        syncAutoplay();
      },
      { signal }
    );
    desktopMediaQuery.addEventListener('change', syncAutoplay, { signal });
    reducedMotionQuery.addEventListener('change', syncAutoplay, { signal });
    requestAnimationFrame(syncAutoplay);

    let viewportRefreshFrame;
    const refreshGalleryLayout = () => {
      if (viewportRefreshFrame !== undefined) cancelAnimationFrame(viewportRefreshFrame);

      viewportRefreshFrame = requestAnimationFrame(() => {
        viewportRefreshFrame = undefined;
        slideshow?.refreshLayout?.();
      });
    };

    window.addEventListener('resize', refreshGalleryLayout, { signal, passive: true });
    window.addEventListener('orientationchange', refreshGalleryLayout, { signal, passive: true });
    window.visualViewport?.addEventListener('resize', refreshGalleryLayout, { signal, passive: true });
    window.addEventListener('pageshow', refreshGalleryLayout, { signal, passive: true });
    signal.addEventListener(
      'abort',
      () => {
        if (viewportRefreshFrame !== undefined) cancelAnimationFrame(viewportRefreshFrame);
      },
      { once: true }
    );
    requestAnimationFrame(refreshGalleryLayout);

    if (!(thumbnailStrip instanceof HTMLElement) || !(progress instanceof HTMLElement) || !progressThumb) return;

    const updateProgress = () => {
      const maxScroll = thumbnailStrip.scrollWidth - thumbnailStrip.clientWidth;

      if (maxScroll <= 1) {
        progress.hidden = true;
        progress.setAttribute('aria-valuenow', '0');
        return;
      }

      progress.hidden = false;
      const progressWidth = progress.clientWidth;
      const thumbWidth = Math.max(44, progressWidth * (thumbnailStrip.clientWidth / thumbnailStrip.scrollWidth));
      const maxThumbOffset = Math.max(progressWidth - thumbWidth, 0);
      const ratio = Math.min(Math.max(thumbnailStrip.scrollLeft / maxScroll, 0), 1);

      progressThumb.style.width = `${thumbWidth}px`;
      progressThumb.style.transform = `translateX(${ratio * maxThumbOffset}px)`;
      progress.setAttribute('aria-valuenow', `${Math.round(ratio * 100)}`);
    };

    const scrollFromPointer = (clientX, grabOffset = null) => {
      const rect = progress.getBoundingClientRect();
      const thumbWidth = progressThumb.getBoundingClientRect().width;
      const maxThumbOffset = Math.max(rect.width - thumbWidth, 1);
      const offset = grabOffset ?? thumbWidth / 2;
      const thumbLeft = Math.min(Math.max(clientX - rect.left - offset, 0), maxThumbOffset);
      const maxScroll = thumbnailStrip.scrollWidth - thumbnailStrip.clientWidth;

      thumbnailStrip.scrollLeft = (thumbLeft / maxThumbOffset) * maxScroll;
    };

    progress.addEventListener(
      'pointerdown',
      (event) => {
        event.preventDefault();
        slideshow?.pause();

        const thumbRect = progressThumb.getBoundingClientRect();
        const grabOffset = event.target === progressThumb ? event.clientX - thumbRect.left : null;
        progress.setPointerCapture(event.pointerId);
        scrollFromPointer(event.clientX, grabOffset);

        const handlePointerMove = (moveEvent) => scrollFromPointer(moveEvent.clientX, grabOffset);
        const handlePointerUp = () => {
          progress.removeEventListener('pointermove', handlePointerMove);
          progress.removeEventListener('pointerup', handlePointerUp);
          progress.removeEventListener('pointercancel', handlePointerUp);
        };

        progress.addEventListener('pointermove', handlePointerMove);
        progress.addEventListener('pointerup', handlePointerUp);
        progress.addEventListener('pointercancel', handlePointerUp);
      },
      { signal }
    );

    progress.addEventListener(
      'keydown',
      (event) => {
        const step = Math.max(thumbnailStrip.clientWidth * 0.45, 72);

        if (event.key === 'ArrowLeft') thumbnailStrip.scrollBy({ left: -step, behavior: 'smooth' });
        else if (event.key === 'ArrowRight') thumbnailStrip.scrollBy({ left: step, behavior: 'smooth' });
        else if (event.key === 'Home') thumbnailStrip.scrollTo({ left: 0, behavior: 'smooth' });
        else if (event.key === 'End') thumbnailStrip.scrollTo({ left: thumbnailStrip.scrollWidth, behavior: 'smooth' });
        else return;

        event.preventDefault();
        slideshow?.pause();
      },
      { signal }
    );

    thumbnailStrip.addEventListener('scroll', updateProgress, { signal, passive: true });
    const resizeObserver = new ResizeObserver(updateProgress);
    resizeObserver.observe(thumbnailStrip);
    signal.addEventListener('abort', () => resizeObserver.disconnect(), { once: true });
    requestAnimationFrame(updateProgress);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller?.abort();
    this.#controller = undefined;
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
